import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { L, imgOf, useFetch, useTitle } from "../lib";
import { PageHeader, ArtImage, SampleTag, Empty, Reveal } from "../ui";
import { useSite } from "../Layout";

function Statement({ text }) {
  const paras = text.split(/\n\s*\n/).filter(Boolean);
  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-10 mb-20" data-testid="artist-statement">
      <Reveal>
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 border-y border-lou-line py-14">
          <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <p className="text-xs tracking-[0.28em] uppercase text-lou-clay">Artist Statement</p>
            <p className="font-lou italic text-3xl sm:text-4xl font-light mt-4 leading-tight">“Seeing nature through my eyes.”</p>
            <p className="text-sm text-lou-soft mt-4">In Louise's own words</p>
          </div>
          <div className="lg:col-span-8 font-lou text-lg sm:text-xl leading-relaxed text-lou-ink/85 space-y-6">
            {paras.map((p, i) => (
              <p key={i} className={i === 0 ? "first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:text-lou-clay" : ""}>{p}</p>
            ))}
            <p className="text-base font-loub text-lou-soft not-italic">Louise Gatanas</p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

const KINDS = [["painting", "Paintings"], ["sculpture", "Sculptures"]];
const FILTERS = [["all", "All works"], ["prints", "Prints available"], ["dated", "Dated"], ["undated", "Undated"]];

export default function Art() {
  useTitle("Her Art");
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") || "painting";
  const [filter, setFilter] = useState("all");
  const { data } = useFetch(`/artworks?kind=${kind}`, [kind]);
  const { site } = useSite();
  const statement = site?.settings?.artist_statement;
  const quote = site?.settings?.art_quote;
  const sculptIntro = site?.settings?.sculpture_intro;
  const items = useMemo(() => (data || []).filter((a) =>
    filter === "prints" ? a.print_available : filter === "dated" ? !!a.year_text : filter === "undated" ? !a.year_text : true), [data, filter]);
  return (
    <div data-testid="art-page">
      <PageHeader eyebrow="Her Art" title="Paintings and sculptures"
        intro="Each work is shown in its full proportions. Select a piece to see every photograph, detail and the stories connected to it." />
      {quote && (
        <Reveal className="max-w-4xl mx-auto px-6 lg:px-10 mb-16">
          <blockquote className="font-lou italic font-light text-2xl sm:text-3xl leading-snug text-lou-ink/90 border-l-2 border-lou-ochre pl-6 sm:pl-10" data-testid="art-quote">
            “{quote}”
            <footer className="font-loub not-italic text-sm text-lou-soft mt-4">Louise Gatanas</footer>
          </blockquote>
        </Reveal>
      )}
      {statement && <Statement text={statement} />}
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-6 border-b border-lou-line pb-4">
          <div role="tablist" aria-label="Collection" className="flex gap-8">
            {KINDS.map(([k, label]) => (
              <button key={k} role="tab" aria-selected={kind === k} data-testid={`art-tab-${k}`}
                onClick={() => { setParams({ kind: k }); setFilter("all"); }}
                className={`font-lou text-2xl pb-2 -mb-[17px] border-b-2 transition-colors ${kind === k ? "border-lou-clay text-lou-ink" : "border-transparent text-lou-soft hover:text-lou-ink"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" aria-label="Filter">
            {FILTERS.filter(([f]) => f !== "prints" || kind === "painting").map(([f, label]) => (
              <button key={f} onClick={() => setFilter(f)} aria-pressed={filter === f} data-testid={`art-filter-${f}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f ? "bg-lou-ink text-lou-paper border-lou-ink" : "border-lou-line hover:border-lou-clay"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {kind === "sculpture" && sculptIntro && (
          <Reveal className="mt-12 max-w-3xl">
            <div data-testid="sculpture-intro">
              <p className="text-xs tracking-[0.28em] uppercase text-lou-clay">Her work in stone</p>
              <div className="font-lou text-lg sm:text-xl leading-relaxed text-lou-ink/85 mt-4 space-y-5">
                {sculptIntro.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
          </Reveal>
        )}
        <div className="mt-12">
          {data && items.length === 0 && <Empty testid="art-empty">No works in this view yet.</Empty>}
          <div className="lou-masonry columns-1 sm:columns-2 lg:columns-3">
            {items.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.05}>
                <Link to={L(`/art/${a.id}`)} className="group block" data-testid={`artwork-card-${a.id}`}>
                  <div className="relative overflow-hidden">
                    <ArtImage src={imgOf(a)} alt={a.title || "Untitled work"} className="transition-transform duration-700 group-hover:scale-[1.015]" />
                    <SampleTag show={a.is_sample} className="absolute top-3 left-3" />
                  </div>
                  <p className="font-lou text-xl mt-4 group-hover:text-lou-clay transition-colors">{a.title || "Untitled"}</p>
                  <p className="text-sm text-lou-soft">{[a.year_text, a.medium].filter(Boolean).join(" · ")}</p>
                  {a.print_available && <p className="text-xs text-lou-clay mt-1 tracking-wide">Prints available on request</p>}
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
